import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useDispatch } from "react-redux";
import { TablePagination } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";

import { usePermissions } from "components/Hook";
import CustomAvatar from "components/common/Avatar";
import CustomSwitch from "components/common/CustomSwitch";
import Text from "components/common/Text";
import SearchableAutoComplete from "components/common/AutoComplete";
import Button from "components/common/Button";
import PageLoader from "components/common/PageLoader";

import { getClockinSettings, updateClockinSettings } from "api/clockInWorkforce";
import { showSnackbar } from "store/actions/Utility";
import { AlertType } from "components/common/Snackbar";
import { baseUrl } from "util/APIBaseUrl";
import { hasPermission } from "hooks/usePermission";

import "./ClockInSettings.scss";

function ClockInSettings() {
  const dispatch = useDispatch();
  const { loggedInUser } = usePermissions();
  const customerId = loggedInUser?.customerId;

  const canConfigureTracking = hasPermission("timeTracking.configure");

  const [employees, setEmployees] = useState([]);
  const [originalState, setOriginalState] = useState({});
  const [localState, setLocalState] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [managerFilter, setManagerFilter] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const fetchEmployees = useCallback(async () => {
    if (!customerId) return;
    try {
      setLoading(true);
      const response = await getClockinSettings(customerId);

      let rows = [];

      if (response?.success && response?.data) {
        rows = response.data?.rows || response.data || [];
      } else if (response?.rows) {
        rows = response.rows;
      } else if (Array.isArray(response?.data)) {
        rows = response.data;
      } else if (Array.isArray(response)) {
        rows = response;
      }

      setEmployees(rows);

      const stateMap = {};
      rows.forEach((emp) => {
        stateMap[emp.employeeId] = !!emp.clockinTrackingEnabled;
      });
      setOriginalState(stateMap);
      setLocalState(stateMap);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Error fetching clock-in settings:", error);
      dispatch(
        showSnackbar({
          type: AlertType.error,
          message: error?.message || "Failed to load clock-in settings",
          dispatch,
        })
      );
    } finally {
      setLoading(false);
    }
  }, [customerId, dispatch]);

  useEffect(() => {
    fetchEmployees();
  }, [fetchEmployees]);

  const handleSearchChange = (e) => {
    const { value } = e.target;
    setSearch(value);
    setPage(0);
  };

  const handleManagerFilterChange = (value) => {
    setManagerFilter(value);
    setPage(0);
  };

  const filteredEmployees = useMemo(() => {
    let result = employees;

    if (search) {
      const searchLower = search.toLowerCase();
      result = result.filter((emp) => {
        const fullName = `${emp.firstName} ${emp.lastName}`.toLowerCase();
        const email = (emp.email || "").toLowerCase();
        return fullName.includes(searchLower) || email.includes(searchLower);
      });
    }

    if (managerFilter) {
      result = result.filter((emp) => emp.manager?.userId === managerFilter);
    }

    return result;
  }, [employees, search, managerFilter]);

  const paginatedEmployees = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredEmployees.slice(start, start + rowsPerPage);
  }, [filteredEmployees, page, rowsPerPage]);

  const managerOptions = useMemo(() => {
    const managersMap = new Map();
    employees.forEach((emp) => {
      const mgr = emp.manager;
      if (mgr?.userId && (mgr.firstName || mgr.lastName)) {
        managersMap.set(mgr.userId, {
          value: mgr.userId,
          label: `${mgr.firstName || ""} ${mgr.lastName || ""}`.trim(),
        });
      }
    });
    return Array.from(managersMap.values());
  }, [employees]);

  const allEnabled = useMemo(() => {
    const ids = Object.keys(localState);
    return ids.length > 0 && ids.every((id) => localState[id]);
  }, [localState]);

  const hasChanges = useMemo(() => {
    return Object.keys(localState).some((id) => localState[id] !== originalState[id]);
  }, [localState, originalState]);

  const handleToggleEmployee = (employeeId) => {
    setLocalState((prev) => ({
      ...prev,
      [employeeId]: !prev[employeeId],
    }));
  };

  const handleToggleAll = () => {
    const newValue = !allEnabled;
    const newState = {};
    Object.keys(localState).forEach((id) => {
      newState[id] = newValue;
    });
    setLocalState(newState);
  };

  const handleCancel = () => {
    setLocalState({ ...originalState });
  };

  const handleSave = async () => {
    const toEnable = [];
    const toDisable = [];

    Object.keys(localState).forEach((id) => {
      if (localState[id] !== originalState[id]) {
        if (localState[id]) {
          toEnable.push(id);
        } else {
          toDisable.push(id);
        }
      }
    });

    if (toEnable.length === 0 && toDisable.length === 0) return;

    try {
      setSaving(true);

      const promises = [];

      if (toEnable.length > 0) {
        promises.push(
          updateClockinSettings(customerId, {
            action: "enable",
            employeeIds: toEnable,
          })
        );
      }

      if (toDisable.length > 0) {
        promises.push(
          updateClockinSettings(customerId, {
            action: "disable",
            employeeIds: toDisable,
          })
        );
      }

      const results = await Promise.all(promises);

      const messages = results.map((r) => r?.message).filter(Boolean);
      dispatch(
        showSnackbar({
          type: AlertType.success,
          message: messages.join(". ") || "Attendance settings updated successfully",
          dispatch,
        })
      );

      setOriginalState({ ...localState });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Error saving clock-in settings:", error);
      dispatch(
        showSnackbar({
          type: AlertType.error,
          message: error?.message || "Failed to update clock-in settings",
          dispatch,
        })
      );
    } finally {
      setSaving(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    const { value } = event.target;
    setRowsPerPage(parseInt(value, 10));
    setPage(0);
  };

  if (loading) {
    return <PageLoader />;
  }

  return (
    <div className="clockin-settings">
      {saving && <PageLoader />}
      <div className="clockin-settings__header">
        <p className="heading-h3-semibold clockin-settings__title">Attendance management</p>
        {hasChanges && (
          <div className="clockin-settings__header-actions">
            <Button label="Cancel" variant="text" color="primary" size="large" onClick={handleCancel} />
            {canConfigureTracking && (
              <Button
                label="Save"
                variant="contained"
                color="primary"
                size="large"
                onClick={handleSave}
                isLoading={saving}
              />
            )}
          </div>
        )}
      </div>

      <div className="clockin-settings__filters">
        <div className="clockin-settings__filter-field">
          <Text
            name="clockinSearch"
            placeholder="Search employees"
            value={search}
            onChange={handleSearchChange}
            size="small"
            startIcon={<SearchIcon className="clockin-settings__search-icon" />}
          />
        </div>
        <div className="clockin-settings__filter-field">
          <SearchableAutoComplete
            name="managerFilter"
            label="Manager"
            placeholder="All managers"
            options={managerOptions}
            value={managerFilter}
            onChange={handleManagerFilterChange}
            isOptional
          />
        </div>
      </div>

      <div className="clockin-settings__card">
        <div className="clockin-settings__card-header">
          <p className="para-body-m-medium clockin-settings__employee-count">{filteredEmployees.length} Employees</p>
          {canConfigureTracking && (
            <div className="clockin-settings__toggle-all">
              <p className="para-body-m-regular clockin-settings__toggle-all-label">Turn on attendance for all</p>
              <CustomSwitch checked={allEnabled} onChange={handleToggleAll} disabled={saving} />
            </div>
          )}
        </div>

        <div className="clockin-settings__list">
          {paginatedEmployees.map((employee) => (
            <div key={employee.employeeId} className="clockin-settings__employee-row">
              <div className="clockin-settings__employee-info">
                <CustomAvatar
                  src={
                    employee.pictureVersion
                      ? `${baseUrl}/employee/${employee.employeeId}/logo?id=${employee.pictureVersion}`
                      : ""
                  }
                  alt={`${employee.firstName} ${employee.lastName}`}
                  size="md"
                />
                <div className="clockin-settings__employee-details">
                  <p className="para-body-m-regular clockin-settings__employee-name">
                    {employee.firstName} {employee.lastName}
                  </p>
                  {employee.email && (
                    <p className="para-body-m-regular clockin-settings__employee-email">{employee.email}</p>
                  )}
                </div>
              </div>

              {canConfigureTracking && (
                <div className="clockin-settings__employee-actions">
                  <CustomSwitch
                    checked={!!localState[employee.employeeId]}
                    onChange={() => handleToggleEmployee(employee.employeeId)}
                    disabled={saving}
                  />
                </div>
              )}
            </div>
          ))}

          {!loading && filteredEmployees.length === 0 && (
            <div className="clockin-settings__empty">
              <p className="para-body-m-regular">No employees found</p>
            </div>
          )}
        </div>
      </div>

      {filteredEmployees.length > 0 && (
        <div className="clockin-settings__pagination">
          <TablePagination
            component="div"
            count={filteredEmployees.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Rows per page:"
            rowsPerPageOptions={[10, 25, 50, 100]}
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} of ${count}`}
          />
        </div>
      )}
    </div>
  );
}

export default ClockInSettings;
